import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, CustomInput, FormFeedback, FormText, Label } from 'reactstrap';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner, faTrashAlt, faUpload, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { uploadFile as rUploadFile } from '../../helpers/requests';
import { fileDownload } from '../../helpers/helpers';
import BaseModal from 'components/shared/BaseModal';
import { ModalFooter } from 'reactstrap';
import SafeBtn from 'components/shared/SafeBtn';
import MontosTableLibroVentas from '../asegurado/credit/LibroVentasTablaMontosConfirmacion'; // Asegúrate de importar el componente MontosTable



const FileUploader = ({
  type,
  uploadUrl,
  preUploadUrl, // Nueva prop opcional
  inputId,
  showLabel,
  label,
  inputText,
  helpText,
  accept,
  handleInitUploading,
  handleResponse,
  disabled,
  existentFile,
  clearable,
  onClearFile,
  solicitada,
  handleSolicitada,
  filename,
  prefijo,
  setFile,
  manualUpload, // Previene que se suba el archivo automaticamente. Espera a que se aprete boton "Subir"
  stateData,
  maxFileSize,
  confirmButtonText,
  showMontosTable = true,
  enableValidationModal,
}) => {
  const loginIdToken = useSelector((state) => state.login.id_token);
  const [maxSizeError, setMaxSizeError] = useState(false);
  const [fileTypeError, setFileTypeError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fileName, setFileName] = useState('');
  const [selectedFileData, setSelectedFileData] = useState(null); // Para el manualUpload
  const [modalData, setModalData] = useState(null); // Estado para manejar el modal
  const [modalOpen, setModalOpen] = useState(false); // Estado para abrir/cerrar el modal
  const customFileInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const isDisabled = stateData.id === 3 || stateData.id === 2 || disabled; // Disable when stateData.id is 2 or 3
  const [selectedIntermediarioIds, setSelectedIntermediarioIds] = useState([]);

  useEffect(() => {
    if (existentFile) {
      setFileName(existentFile.name);
    }
  }, [existentFile]);

  const toggleModal = () => setModalOpen(!modalOpen);

  const verifyAndUploadFile = async (file, fileObj) => {
    setUploading(true);
    try {
      if (preUploadUrl) {
        if (enableValidationModal) {
          setModalData({ status: "loading" });
          setModalOpen(true);
        }
        const response = await rUploadFile(preUploadUrl, file, fileObj);

        if (response.status === "success") {
          setModalData({ status: "success", datos: response.datos, message: response.message, warnings: response.warnings || [], file, fileObj });
          setSelectedIntermediarioIds((response.datos?.intermediarios || []).map((i) => i.id));
          if (!enableValidationModal) setModalOpen(true);
        } else {
          setModalData({ status: "error", errors: response.errors });
          setModalOpen(true);
        }
        setModalOpen(true);
      } else {
        // Si no hay preUploadUrl, proceder directamente con la subida
        await uploadFile(file, fileObj);
      }
    } catch (err) {
      handleResponse({ ...err, file });
    } finally {
      setUploading(false);
    }
  };

  const uploadFile = (file, fileObj, urlOverride) => {
    setUploading(true);
    handleInitUploading();
    return rUploadFile(urlOverride || uploadUrl, file, fileObj)
      .then((resp) => {
        setSuccess(true);
        handleResponse({ ...resp, file });
        setModalOpen(false); // Cerrar modal al terminar
      })
      .catch((err) => {
        handleResponse({ ...err, file });
      })
      .finally(() => {
        setFileName(file);
        setUploading(false);
      });
  };

  const handleFileChange = (e) => {
    const { files } = e.target;
    if (files.length === 0) return;
    setSuccess(false);
    setUploading(false);
    setLoadingBtn(false);
    let validFile = true;

    const limit = maxFileSize || 1e8;

    if (files[0].size > limit) {
      setMaxSizeError(true);
      validFile = false;
  }

    if (!validateFileType(files[0])) {
      setFileTypeError(true);
      validFile = false;
    };

    if (!validFile) return;

    setMaxSizeError(false);
    setFileTypeError(false);

    const reader = new FileReader();
    const name = prefijo ? prefijo.concat('_', e.target.files[0].name) : e.target.files[0].name;
    if (customFileInputRef && customFileInputRef.current) {
      customFileInputRef.current.labels[0].textContent = name;
    }
    reader.readAsArrayBuffer(files[0]);
    reader.onload = (event) => {
      setSuccess(false);
      if (setFile) {
        setFile(event.target.result);
      }

      if (manualUpload) {
        setSelectedFileData({ name: name, data: event.target.result, originalFile: files[0] });
      } else {
        verifyAndUploadFile(name, event.target.result);
      }
    };
  };

  const validateFileType = (file) => {
    if (!accept) return true;

    const acceptedTypes = accept.split(',').map(type => type.trim());

    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    return acceptedTypes.some((acceptedType) => {
      if (acceptedType.startsWith('.')) {
        // Extension check (".xlsx", ".pdf")
        return fileExtension === acceptedType.toLowerCase();
      } else if (acceptedType.includes('/')) {
        // MIME type check ("image/jpeg", "application/pdf")
        return file.type === acceptedType;
      } else if (acceptedType.includes('*')) {
        // MIME with wildcard type check ("image/*", "audio/*")
        const baseType = acceptedType.split('/')[0];
        return file.type.startsWith(baseType);
      }

      // Unknown file type
      return false;
    });
  }

  const triggerManualUpload = () => {
    if (selectedFileData) {
      verifyAndUploadFile(selectedFileData.name, selectedFileData.data);
    }
  };

  const closeModal = () => setModalOpen(false);

  const confirmUpload = () => {
    setLoadingBtn(true);
    if (modalData && modalData.file && modalData.fileObj) {
      const finalUrl = modalData.datos?.intermediarios?.length
        ? `${uploadUrl}?notify_intermediario_ids=${encodeURIComponent(JSON.stringify(selectedIntermediarioIds))}`
        : uploadUrl;
      uploadFile(modalData.file, modalData.fileObj, finalUrl);
    }
  };

  const clearInput = () => {
    if (customFileInputRef?.current) {
      customFileInputRef.current.value = '';
      customFileInputRef.current.labels[0].textContent = inputText;
    }

    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }

    setFileName('');
    setSelectedFileData(null);
    setSuccess(false);
    onClearFile();
  };

  useEffect(() => {
    if (solicitada) {
      clearInput();
      handleSolicitada();
    }
  }, [solicitada]);

  useEffect(() => {
    setFileName(filename);
  }, [filename]);

  useEffect(() => {
    if (modalOpen && modalData?.status === "error") {
      clearInput();
    }
  }, [modalOpen]);

  const templateCols = () => {
    let template = '1fr';
    if (existentFile) {
      template += ' auto';
    }
    if (clearable && fileName) {
      template += ' auto';
    }
    if (manualUpload) {
      template += ' auto';
    }
    return template;
  };

  const getLabel = () => {
    if (filename !== '') {
      if (customFileInputRef && customFileInputRef.current) {
        customFileInputRef.current.labels[0].textContent = fileName;
      }
      return fileName;
    }
    return uploading ? 'Subiendo...' : fileName || inputText;
  };

  const inputType = () => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: templateCols(),
      }}
    >
      <div>
        {showLabel && <Label for={inputId}>{label}</Label>}
        <CustomInput
          style={{ cursor: disabled ? 'default' : 'pointer' }}
          innerRef={customFileInputRef}
          type="file"
          id={inputId}
          name="file-uploader"
          label={getLabel()}
          onChange={handleFileChange}
          invalid={maxSizeError || fileTypeError}
          accept={accept}
          disabled={disabled}
        >
          {maxSizeError && (
            <FormFeedback>
              El archivo supera el tamaño máximo permitido (
              {((maxFileSize || 1e8) / 1024 / 1024).toFixed(1)} MB).
            </FormFeedback>
          )}
          {fileTypeError && (
            <FormFeedback>El formato del archivo no es compatible con los formatos aceptados ({accept})</FormFeedback>
          )}
        </CustomInput>
        {helpText && <FormText color="muted">{helpText}</FormText>}
      </div>
      {existentFile && (
        <div>
          <Button
            type="button"
            color="orsan-link"
            onClick={() => fileDownload(existentFile.downloadUrl, loginIdToken)}
            title="Descargar"
          >
            <FontAwesomeIcon icon={faDownload} />
          </Button>
        </div>
      )}
      {clearable && fileName && (
        <div>
          <Button
            type="button"
            color="orsan-link"
            onClick={() => clearInput()}
            title="Limpiar"
            style={{
              color: '#000',
              opacity: '.5',
            }}
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </Button>
        </div>
      )}
      {manualUpload && (
        <div>
          <Button
            type="button"
            className="btn btn-orsan-primary ml-4"
            onClick={triggerManualUpload}
            disabled={isDisabled || uploading || success || !selectedFileData}
            title={success ? "Archivo subido" : "Subir Archivo seleccionado"}
          >
            {uploading ? (
              <FontAwesomeIcon icon={faSpinner} pulse style={{ marginRight: '6px' }} />
            ) : (
              !success && <FontAwesomeIcon icon={faUpload} style={{ marginRight: '6px' }} />
            )}
            {uploading ? 'Subiendo...' : (success ? <> <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px' }}/> Subido </> : 'Subir')}
          </Button>
        </div>
      )}
    </div>
  );

  const getLinkColor = () => {
    let color = '#0055B8'; // Default blue color
    if (isDisabled) {
      color = '#CCCCCC'; // Gray color when disabled
    } else if (!uploading && (maxSizeError || fileTypeError)) {
      color = '#dc3545'; // Red color for max size error
    } else if (!uploading && success) {
      color = '#25983F'; // Green color for success
    }
    return color;
  };
  
  const linkType = () => (
    <>
      {/* Solo mostrar el botón si el estado no es 2 ni 3 */}
      {!(stateData.id === 2 || stateData.id === 3) && (
        <label
          htmlFor={inputId}
          style={{
            fontSize: '18px',
            color: getLinkColor(),
            cursor: isDisabled ? 'default' : 'pointer',
          }}
          title={isDisabled ? 'No puede ingresar más libros, contáctese con...' : ''}
        >
          {uploading ? (
            <FontAwesomeIcon icon={faSpinner} pulse />
          ) : (
            <FontAwesomeIcon icon={faUpload} />
          )}
          {!isDisabled && (
            <input
              style={{ display: 'none' }}
              ref={fileInputRef}
              type="file"
              id={inputId}
              name="file-uploader"
              onChange={handleFileChange}
              accept={accept}
            />
          )}
        </label>
      )}
    </>
  );

  return (
    <>
      {type === "link" ? linkType() : inputType()}
  
      <BaseModal
        modalOpen={modalOpen}
        handleModal={toggleModal}
        title={
          enableValidationModal ? (
            modalData?.status === "loading" ? "Validando archivo..." :
            modalData?.status === "success" ? "Validación Exitosa" :
            "Errores de Validación"
          ) : (
            modalData?.status === "success" ? "Validación Exitosa" : "Errores de Validación"
          )
        }
        type="custom-modal"
        backdrop="static"
      >
        {/* LOADING — solo con el prop */}
        {enableValidationModal && modalData?.status === "loading" && (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <FontAwesomeIcon icon={faSpinner} pulse size="3x" style={{ color: "#0055B8", marginBottom: 16 }} />
            <p style={{ fontSize: 16, color: "#555", marginTop: 12 }}>
              Validando archivo, por favor espere...
            </p>
          </div>
        )}

        {modalData?.status === "success" && (
          <div>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#e6f7e6",
              border: "1px solid #28a745",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
              color: "#4a4a4a",
              fontSize: "16px",
              fontWeight: "500",
            }}>
              <span style={{ marginRight: "10px" }}>{modalData.message}</span>
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#28a745", fontSize: "20px" }} />
            </div>
          
            {/* WARNINGS — hay que darle el prop */}
            {enableValidationModal && modalData?.warnings?.length > 0 && (
              <div style={{
                backgroundColor: "#fff8e1",
                border: "1px solid #f0ad4e",
                borderRadius: "8px",
                padding: "15px",
                marginBottom: "16px",
              }}>
                <p style={{ fontWeight: 600, marginBottom: 8, color: '#856404' }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 6, color: '#f0ad4e' }} />
                  El archivo puede subirse, pero se encontraron las siguientes alertas:
                </p>
                <ul style={{ paddingLeft: "20px", listStyleType: "disc", overflowY: "auto", maxHeight: "200px", marginBottom: 0 }}>
                  {modalData.warnings.map((w, i) => (
                    <li key={i} style={{ fontSize: "14px", color: "#856404", marginBottom: "6px" }}>
                      {w.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {modalData?.datos?.intermediarios?.length > 0 && (
              <div style={{
                backgroundColor: "#f5f7fa",
                border: "1px solid #dde2e8",
                borderRadius: "8px",
                padding: "15px",
                marginBottom: "16px",
                textAlign: "left",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <p style={{ fontWeight: 600, margin: 0, color: "#333" }}>
                    Notificar por correo a:
                  </p>
                  <Button
                    color="link"
                    style={{ padding: 0, fontSize: 13 }}
                    onClick={() =>
                      setSelectedIntermediarioIds((prev) =>
                        prev.length === modalData.datos.intermediarios.length
                          ? []
                          : modalData.datos.intermediarios.map((i) => i.id)
                      )
                    }
                  >
                    {selectedIntermediarioIds.length === modalData.datos.intermediarios.length
                      ? "Deseleccionar todos"
                      : "Seleccionar todos"}
                  </Button>
                </div>
                {modalData.datos.intermediarios.map((i) => (
                  <div key={i.id} className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`notify-intermediario-${i.id}`}
                      checked={selectedIntermediarioIds.includes(i.id)}
                      onChange={() =>
                        setSelectedIntermediarioIds((prev) =>
                          prev.includes(i.id) ? prev.filter((id) => id !== i.id) : [...prev, i.id]
                        )
                      }
                    />
                    <label className="form-check-label" htmlFor={`notify-intermediario-${i.id}`} style={{ fontSize: 14 }}>
                      {i.nombre} ({i.rut})
                    </label>
                  </div>
                ))}
              </div>
            )}
            {showMontosTable && (
              <div style={{ padding: "15px", borderRadius: "8px", overflowY: "auto", maxHeight: "calc(100vh - 25rem)" }}>
                <MontosTableLibroVentas montos={modalData.datos.montos} />
              </div>
            )}
          </div>
        )}

        {/* ERROR */}
        {modalData?.status === "error" && (
          <div style={{ padding: "20px" }}>
            <ul style={{ paddingLeft: "20px", listStyleType: "disc", overflowY: "auto", maxHeight: "calc(100vh - 25rem)" }}>
              {modalData?.errors.map((error, index) => (
                <li key={index} style={{ fontSize: "16px", color: "#d9534f", marginBottom: "10px" }}>
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}
        <ModalFooter>
          {modalData?.status === "success" && (
            <>
              <Button className="btn-orsan-secondary mr-auto" onClick={closeModal} disabled={loadingBtn} style={{ width: 165, marginLeft: 19 }}>
                Cancelar
              </Button>
              <SafeBtn className="btn-orsan-primary" onClick={confirmUpload} disabled={loadingBtn} loading={loadingBtn} style={{ width: 165, marginRight: 19 }}>
                {confirmButtonText || "Cargar"}
              </SafeBtn>
            </>
          )}
          {modalData?.status === "error" && (
            <Button className="btn-orsan-secondary mr-auto" onClick={closeModal} disabled={loadingBtn} style={{ width: 165, marginLeft: 19 }}>
              Cerrar
            </Button>
          )}
        </ModalFooter>
      </BaseModal>
    </>
  );
};

FileUploader.propTypes = {
  uploadUrl: PropTypes.string.isRequired,
  preUploadUrl: PropTypes.string,
  inputId: PropTypes.string,
  showLabel: PropTypes.bool,
  label: PropTypes.string,
  inputText: PropTypes.string,
  helpText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  accept: PropTypes.string,
  handleInitUploading: PropTypes.func,
  handleResponse: PropTypes.func,
  type: PropTypes.oneOf(['input', 'link']),
  disabled: PropTypes.bool,
  existentFile: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    downloadUrl: PropTypes.string,
  }),
  clearable: PropTypes.bool,
  onClearFile: PropTypes.func,
  solicitada: PropTypes.bool,
  handleSolicitada: PropTypes.func,
  filename: PropTypes.string,
  prefijo: PropTypes.string,
  setFile: PropTypes.func,
  manualUpload: PropTypes.bool,
  stateData: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  maxFileSize: PropTypes.number,
  confirmButtonText: PropTypes.string,
  showMontosTable: PropTypes.bool,
  enableValidationModal: PropTypes.bool,
};

FileUploader.defaultProps = {
  preUploadUrl: null,
  inputId: 'file-uploader',
  showLabel: false,
  label: 'Archivo',
  inputText: 'Seleccione un archivo',
  helpText: '',
  accept: undefined,
  handleInitUploading: () => {},
  handleResponse: () => {},
  type: 'input',
  disabled: false,
  existentFile: null,
  clearable: false,
  onClearFile: () => {},
  filename: '',
  prefijo: '',
  setFile: () => {},
  manualUpload: false,
  stateData: '',
  maxFileSize: 1e8,
  confirmButtonText: 'Cargar',
  showMontosTable: true,
  enableValidationModal: false
};

export default FileUploader;
